<template>
  <v-treeview
    :items="computedWatermeters"
    item-key="name"
    item-children="sub_watermeters"
    class="border rounded-lg elevation-4"
  >
    <template #label="{ item }">
      <v-row
        no-gutters
        class="justify-start align-center border-bottom"
        @click="openWatermeterCard(item.watermeter_id)"
      >
        <v-col cols="auto">
          <v-row no-gutters>
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <v-col v-on="on" cols="auto" class="text-body-2 pt-1">
                  {{ item.watermeter_serial_number }}
                </v-col>
              </template>
              {{ serialNumberVerboseName }}
            </v-tooltip>
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <v-col v-on="on" cols="auto" class="pl-1">
                  <v-icon small color="primary">
                    mdi-information
                  </v-icon>
                </v-col>
              </template>
              {{ getTooltipContent(item.watermeter_kind) }}
            </v-tooltip>
          </v-row>
          <v-row no-gutters>
            <v-tooltip bottom color="font">
              <template #activator="{ on }">
                <v-col v-on="on" cols="auto" class="text-body-2 billing-id-text">
                  {{ item.billing_id }}
                </v-col>
              </template>
              {{ $i18n.t('featureCard.bilingSystemId') }}
            </v-tooltip>
          </v-row>
        </v-col>
      </v-row>
    </template>
    <template #append="{ item }" v-if="hasAppendSlot">
      <v-row dense class="justify-start align-center border-bottom" @click="openWatermeterCard(item.watermeter_id)">
        <v-col cols="auto" v-if="hasLegalization">
          <v-tooltip bottom color="font">
            <template #activator="{ on }">
              <v-chip v-on="on" label small :color="getChipColor(item.legalization_date)">
                <span class="text-caption white--text font-weight-medium">
                  {{ getLegalizationDate(item.legalization_date) }}
                </span>
              </v-chip>
            </template>
            <span>
              {{ $i18n.t('featureCard.legalizationDate') }}
            </span>
          </v-tooltip>
        </v-col>
        <v-col cols="auto" v-if="hasWaterUsage">
          <v-tooltip bottom color="font">
            <template #activator="{ on }">
              <v-chip v-on="on" label small color="primary">
                <span class="text-caption font-weight-medium" v-html="getWaterUsage(item.water_usage)"> </span>
              </v-chip>
            </template>
            <span>
              {{ $i18n.t('featureCard.waterUsage') }}
            </span>
          </v-tooltip>
        </v-col>
        <v-col cols="auto" v-if="item.level === 0">
          <dots-menu :items="dotsMenu" @deleteAssignment="deleteAssignment(item)" font-size="19px"> </dots-menu>
        </v-col>
      </v-row>
    </template>
  </v-treeview>
</template>

<script>
import { get } from 'vuex-pathify';

import DotsMenu from '@/components/DotsMenu';

export default {
  name: 'WidgetPartitionsWatermetersTreeview',
  components: {
    DotsMenu,
  },
  props: {
    watermeters: {
      type: Array,
      required: true,
    },
    hasLegalization: {
      type: Boolean,
      default: false,
    },
    hasWaterUsage: {
      type: Boolean,
      default: false,
    },
    hasWatermeterKind: {
      type: Boolean,
      default: false,
    },
    hasParentWatemeter: {
      type: Boolean,
      default: false,
    },
  },
  data: () => ({
    dotsMenu: [
      {
        name: 'deleteAssignment',
      },
    ],
  }),
  computed: {
    watermetersLayerId: get('admin/modulesMapping@billing.layer_id'),
    hasAppendSlot() {
      return this.hasWaterUsage || this.hasLegalization;
    },
    computedWatermeters() {
      return this.getWatermetersLevels(this.watermeters, 0);
    },
    billingsLegalizationSoonThreshold: get('admin/settingsValues@billings_module_legalization_soon_threshold'),
    watermeterSerialNumberVerboseName: get('admin/settingsValues@billings_serial_number_display_value'),
    serialNumberVerboseName() {
      return this.watermeterSerialNumberVerboseName || this.$i18n.t('featureCard.watermeterSerialNumber');
    },
  },
  methods: {
    getChipColor(date) {
      if (!date) {
        return this.$_colors.italic;
      }
      if (this.$dayjs(date).isAfter(this.threshholdedDate)) {
        return this.$_colors.success;
      } else if (this.$dayjs(date).isBefore(this.today)) {
        return this.$_colors.error;
      }
      return this.$_colors.edit;
    },
    getWaterUsage(waterUsage) {
      return `${waterUsage} m<sup>3</sup>`;
    },
    getLegalizationDate(date) {
      return this.$dayjs(date).format('L');
    },
    getTooltipContent(type) {
      return this.$i18n.t('featureCard.type', { type });
    },
    deleteAssignment(id) {
      this.$emit('deleteAssignment', id);
    },
    getWatermetersLevels(watermeters, level) {
      return watermeters.map(watermeter => {
        if (watermeter.sub_watermeters?.length > 0) {
          return {
            ...watermeter,
            level,
            sub_watermeters: this.getWatermetersLevels(watermeter.sub_watermeters, level + 1),
          };
        }
        return { ...watermeter, level };
      });
    },
    openWatermeterCard(id) {
      this.$router.push({
        name: 'layerFeature',
        params: {
          lid: this.watermetersLayerId,
          fid: id,
        },
      });
    },
  },
  created() {
    this.today = this.$dayjs();
    this.threshholdedDate = this.$dayjs().add(this.billingsLegalizationSoonThreshold, 'day');
  },
};
</script>

<style lang="scss" scoped>
.border {
  @include border();
}
::v-deep {
  .v-chip {
    padding: 0 6px;
  }
  .v-treeview-node:not(:last-child) {
    @include border(bottom);
  }
  .v-treeview-node__level:not(:last-child) {
    width: 24px;
  }
}
.billing-id-text {
  font-family: Roboto;
  font-style: italic;
  font-weight: 300;
  font-size: 12px;
  line-height: 16px;
  color: rgba(0, 0, 0, 0.59);
}
</style>
